import {Component, ElementRef, HostListener, input, InputSignal, output, OutputEmitterRef, signal, ViewChild, OnInit, computed} from '@angular/core';
import {AbstractControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {Store} from '@ngrx/store';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {CommonService} from '../../../services/common.service';
import {MatCardModule} from '@angular/material/card';

@Component({
  selector: 'app-password-input',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatCardModule
  ],
  templateUrl: './password-input.component.html',
  styleUrls: ['../styles.css', './password-input.component.css']
})
export class PasswordInputComponent implements OnInit {
  private readonly onDestroy: Subject<any> = new Subject<any>();

  label: InputSignal<any> = input('');
  type: InputSignal<any> = input('');
  placeholder: InputSignal<any> = input('');
  parentFormGroup: InputSignal<FormGroup<any>> = input(new FormGroup({}));
  controlName: InputSignal<any> = input('');
  error: InputSignal<any> = input('');
  showHint: InputSignal<boolean> = input(true);
  isConfirmPassword: InputSignal<boolean> = input(false);
  hideSubscript: InputSignal<any> = input(false);
  autocomplete: InputSignal<any> = input(true);
  boundingContainer = input<HTMLElement | undefined | null>(undefined);
  checkForErrors: OutputEmitterRef<any> = output();

  showPassword = signal(false);
  showHints = signal(false);
  popupPosition = signal<'left' | 'right' | 'top' | 'bottom'>('left');

  // Password validation signals
  currentPassword = signal('');
  hasMinLength = computed(() => this.currentPassword().length >= 6);
  hasMaxLength = computed(() => this.currentPassword().length <= 20);
  hasSpecialChar = computed(() => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(this.currentPassword()));
  hasNumber = computed(() => /\d/.test(this.currentPassword()));
  hasUpperCase = computed(() => /[A-Z]/.test(this.currentPassword()));
  hasLowerCase = computed(() => /[a-z]/.test(this.currentPassword()));

  // For confirm password
  confirmPasswordMatches = signal(false);

  fieldSubscription: Subscription | undefined;

  isRequired: boolean = false;
  isDisabled: boolean = false;

  @ViewChild('passwordInput') passwordInput!: ElementRef;

  constructor(
    private commonService: CommonService,
    private store: Store,
    private elementRef: ElementRef
  ) {}

  // Do not show field as touched and having error when clicked outside the field
  @HostListener("focusout", ["$event"])
  onBlur() {
    // Close hints when focus moves away
    this.showHints.set(false);

    if (!this.error()) {
      // @ts-ignore
      this.parentFormGroup()?.controls[this.controlName()].markAsUntouched();
    }
  }

  // Close hints when clicking anywhere except the password field
  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    // Check if the click is within this component
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showHints.set(false);
    }
  }

  // Handle tab key
  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.showHints.set(false);
    }
  }

  ngOnInit(): void {
    // Call updatePopupPosition after component initializes
    setTimeout(() => {
      this.updatePopupPosition();
    }, 200);

    // @ts-ignore
    const control = this.parentFormGroup()?.controls[this.controlName()];

    this.isDisabled = control?.status === "DISABLED";
    this.fieldSubscription = control?.valueChanges?.subscribe((value) => {
      // Update current password value for validation
      this.currentPassword.set(value || '');

      // Check if confirm password matches
      if (this.isConfirmPassword()) {
        const passwordControl = this.parentFormGroup().get('password');
        const confirmPasswordControl = this.parentFormGroup().get(this.controlName());

        if (passwordControl && confirmPasswordControl) {
          this.confirmPasswordMatches.set(passwordControl.value === confirmPasswordControl.value);
        }
      }

      // If field is marked as touched, mark it as untouched
      if (control?.touched) {
        control?.markAsUntouched();
      }

      // Get errors on the form field
      const errors = control?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        control?.setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName());

        // Add errors again to the form field for showing messages in the future
        control?.setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName());
      }
    });

    // For confirm password field, also listen to changes in the password field
    if (this.isConfirmPassword()) {
      const passwordControl = this.parentFormGroup().get('password');
      if (passwordControl) {
        passwordControl.valueChanges.subscribe(() => {
          const confirmPasswordControl = this.parentFormGroup().get(this.controlName());
          if (confirmPasswordControl) {
            this.confirmPasswordMatches.set(passwordControl.value === confirmPasswordControl.value);
          }
        });
      }
    }

    // Check if field is required
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator["required"]);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.showHints()) {
      this.updatePopupPosition();
    }
  }

  togglePasswordVisibility() {
    this.showPassword.set(!this.showPassword());
  }

  onFocus() {
    // Only show hints when actually focused, not when autofilled
    setTimeout(() => {
      const activeElement = document.activeElement;
      const inputElement = this.passwordInput?.nativeElement;
      if (activeElement === inputElement) {
        this.showHints.set(true);
        this.updatePopupPosition();
      }
    }, 100);
  }

  private updatePopupPosition() {
    if (!this.passwordInput || !this.passwordInput.nativeElement) {
      this.popupPosition.set('bottom');
      return;
    }

    const inputElRect = this.passwordInput.nativeElement.getBoundingClientRect();
    const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);

    // Check if we're in a narrow container like a sidebar
    const isNarrow = this.isInNarrowContainer();

    // If in a narrow container, prioritize top/bottom placement to avoid overflow
    if (isNarrow) {
      // If there's enough space above, place it there, otherwise below
      const spaceAbove = inputElRect.top;
      const estimatedPopupHeight = this.isConfirmPassword() ? 100 : 200; // Simplified height estimate

      if (spaceAbove > estimatedPopupHeight) {
        this.popupPosition.set('top');
      } else {
        this.popupPosition.set('bottom');
      }
      return;
    }

    // For normal (wider) containers, continue with the original positioning logic
    const popupWidthInRem = 30;
    const horizontalOffsetMaintainedByCss = 2;
    const spaceNeededForHorizontalPopup = (popupWidthInRem + horizontalOffsetMaintainedByCss) * remInPx;

    const verticalMarginInRem = 0.8;
    let estimatedPopupContentHeightInRem: number;
    if (this.isConfirmPassword()) {
      estimatedPopupContentHeightInRem = 5;
    } else {
      estimatedPopupContentHeightInRem = 16;
    }
    const spaceNeededForVerticalPopup = (estimatedPopupContentHeightInRem + verticalMarginInRem) * remInPx;

    let containerRect: DOMRect;
    let containerScrollTop = 0;
    let containerScrollLeft = 0;
    let containerClientWidth: number;
    let containerClientHeight: number;

    const container = this.boundingContainer();
    if (container) {
      containerRect = container.getBoundingClientRect();
      containerScrollTop = container.scrollTop;
      containerScrollLeft = container.scrollLeft;
      containerClientWidth = container.clientWidth;
      containerClientHeight = container.clientHeight;
    } else {
      containerRect = new DOMRect(0, 0, window.innerWidth, window.innerHeight);
      containerScrollTop = window.scrollY;
      containerScrollLeft = window.scrollX;
      containerClientWidth = window.innerWidth;
      containerClientHeight = window.innerHeight;
    }

    const inputTopRelativeToContainer = (inputElRect.top - containerRect.top) + containerScrollTop;
    const inputLeftRelativeToContainer = (inputElRect.left - containerRect.left) + containerScrollLeft;
    const inputRightRelativeToContainer = (inputElRect.right - containerRect.left) + containerScrollLeft;
    const inputBottomRelativeToContainer = (inputElRect.bottom - containerRect.top) + containerScrollTop;

    let chosenPosition: 'left' | 'right' | 'top' | 'bottom' = 'bottom';

    if (inputLeftRelativeToContainer >= spaceNeededForHorizontalPopup) {
      chosenPosition = 'left';
    } else if ((containerClientWidth - inputRightRelativeToContainer) >= spaceNeededForHorizontalPopup) {
      chosenPosition = 'right';
    } else if (inputTopRelativeToContainer >= spaceNeededForVerticalPopup) {
      chosenPosition = 'top';
    } else if ((containerClientHeight - inputBottomRelativeToContainer) >= spaceNeededForVerticalPopup) {
      chosenPosition = 'bottom';
    }

    this.popupPosition.set(chosenPosition);
  }

  // Check if the component is in a narrow container (like a sidebar)
  isInNarrowContainer(): boolean {
    const container = this.boundingContainer();
    if (!container) {
      return false;
    }

    // If container width is less than 500px, consider it narrow
    const containerWidth = container.clientWidth || 0;
    return containerWidth < 500;
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
